import { ReactNode, useMemo } from "react";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import BaseModalView from "./BaseModalView";

import { useTheme } from "@/providers/ThemeProvider";

/**
 * Props for the BaseModalContainer component
 */
interface BaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  size?: "xs" | "sm" | "md" | "lg" | "full";
  headerContent?: ReactNode;
  children: ReactNode;
  minHeight?: string;
  maxHeight?: string;
  /**
   * Whether clicking the overlay/backdrop dismisses the modal.
   * @default true
   */
  closeOnOverlayClick?: boolean;
}

/**
 * Container component that provides a reusable modal with theme and safe area support
 * @param root0 - Component props
 * @param root0.isOpen - Boolean flag indicating if the modal is currently visible
 * @param root0.onClose - Callback function invoked when the modal is closed
 * @param root0.title - Title text displayed in the modal header
 * @param root0.size - Size variant of the modal (xs, sm, md, lg, or full)
 * @param root0.headerContent - Optional additional content to display in the header
 * @param root0.children - Content to be rendered inside the modal body
 * @param root0.minHeight - Minimum height of the modal (web only)
 * @param root0.maxHeight - Maximum height of the modal (web only)
 * @param root0.closeOnOverlayClick - Whether backdrop click dismisses the modal
 */
const BaseModalContainer = ({
  isOpen,
  onClose,
  title,
  size = "lg",
  headerContent,
  children,
  minHeight = "60vh",
  maxHeight = "80vh",
  closeOnOverlayClick = true,
}: BaseModalProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = useMemo(() => resolvedTheme === "dark", [resolvedTheme]);
  const insets = useSafeAreaInsets();

  return (
    <BaseModalView
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size={size}
      headerContent={headerContent}
      minHeight={minHeight}
      maxHeight={maxHeight}
      closeOnOverlayClick={closeOnOverlayClick}
      isDark={isDark}
      topInset={insets.top}
    >
      {children}
    </BaseModalView>
  );
};

export default BaseModalContainer;
